import { useState } from 'react'
import {
	ACCESSIBILITY,
	type Accessibility,
	ACTIVITY_TYPE,
	type ActivityType,
	AUDIENCE,
	type Audience,
	COST,
	type Cost,
	WEATHER_SUIT,
	type WeatherSuit,
} from '@hewey/shared'
import { FilterBar, type FilterFacet } from '@components/FilterBar.tsx'
import barStyles from '@components/FilterBar.module.css'
import type { GeoState } from '@hooks/useGeolocation.ts'
import { DistanceFilterDialog } from './DistanceFilterDialog.tsx'
import {
	ACCESSIBILITY_META,
	ACTIVITY_TYPE_META,
	AUDIENCE_META,
	COST_META,
	WEATHER_SUIT_META,
} from './activityLabels.ts'
import {
	type ActivityFilter,
	activeFilterCount,
	type WhereFilter,
} from './filter.ts'

type Props = {
	filter: ActivityFilter
	setFilter: (next: ActivityFilter) => void
	geo: GeoState
	onRetryLocation: () => void
}

const WHERE_LABEL: Record<WhereFilter, string> = {
	all: 'Where',
	home: '🏠 Home',
	place: '📍 Out',
}

const WHERE_NEXT: Record<WhereFilter, WhereFilter> = {
	all: 'home',
	home: 'place',
	place: 'all',
}

const toggle = <T,>(arr: readonly T[], v: T): T[] =>
	arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]

export const ActivityFilterBar = ({
	filter,
	setFilter,
	geo,
	onRetryLocation,
}: Props) => {
	const [distanceOpen, setDistanceOpen] = useState(false)

	const facets: FilterFacet[] = [
		{
			key: 'weather',
			label: 'Weather',
			options: WEATHER_SUIT.map((w) => ({
				value: w,
				label: WEATHER_SUIT_META[w].label,
				emoji: WEATHER_SUIT_META[w].emoji,
			})),
			selected: filter.weathers,
			onToggle: (v) =>
				setFilter({
					...filter,
					weathers: toggle(filter.weathers, v as WeatherSuit),
				}),
			onClear: () => setFilter({ ...filter, weathers: [] }),
		},
		{
			key: 'type',
			label: 'Type',
			options: ACTIVITY_TYPE.map((t) => ({
				value: t,
				label: ACTIVITY_TYPE_META[t].label,
				emoji: ACTIVITY_TYPE_META[t].emoji,
			})),
			selected: filter.types,
			onToggle: (v) =>
				setFilter({
					...filter,
					types: toggle(filter.types, v as ActivityType),
				}),
			onClear: () => setFilter({ ...filter, types: [] }),
		},
		{
			key: 'cost',
			label: 'Cost',
			options: COST.map((c) => ({
				value: c,
				label: COST_META[c].label,
				emoji: COST_META[c].emoji,
			})),
			selected: filter.costs,
			onToggle: (v) =>
				setFilter({
					...filter,
					costs: toggle(filter.costs, v as Cost),
				}),
			onClear: () => setFilter({ ...filter, costs: [] }),
		},
		{
			key: 'audience',
			label: 'Audience',
			options: AUDIENCE.map((a) => ({
				value: a,
				label: AUDIENCE_META[a].label,
				emoji: AUDIENCE_META[a].emoji,
			})),
			selected: filter.audiences,
			onToggle: (v) =>
				setFilter({
					...filter,
					audiences: toggle(filter.audiences, v as Audience),
				}),
			onClear: () => setFilter({ ...filter, audiences: [] }),
		},
		{
			key: 'accessibility',
			label: 'Access',
			options: ACCESSIBILITY.map((x) => ({
				value: x,
				label: ACCESSIBILITY_META[x].label,
				emoji: ACCESSIBILITY_META[x].emoji,
			})),
			selected: filter.accessibilities,
			onToggle: (v) =>
				setFilter({
					...filter,
					accessibilities: toggle(
						filter.accessibilities,
						v as Accessibility,
					),
				}),
			onClear: () => setFilter({ ...filter, accessibilities: [] }),
		},
	]

	// Distance (custom slider dialog) and Where (inline tri-state cycle) don't
	// fit the multi-select facet model, so they ride in bespoke bar slots.
	// Distance leads the bar — it's one of the most-used filters — while Where
	// trails the facets.
	const distanceTrigger = (
		<button
			type="button"
			className={`${barStyles.trigger} ${filter.maxDistanceMiles !== null ? barStyles.triggerActive : ''}`.trim()}
			onClick={() => setDistanceOpen(true)}
		>
			{filter.maxDistanceMiles !== null
				? `Within ${filter.maxDistanceMiles} mi`
				: 'Distance'}
		</button>
	)

	const whereTrigger = (
		<button
			type="button"
			className={`${barStyles.trigger} ${filter.where !== 'all' ? barStyles.triggerActive : ''}`.trim()}
			onClick={() =>
				setFilter({ ...filter, where: WHERE_NEXT[filter.where] })
			}
			aria-label={`Where filter: ${filter.where}`}
		>
			{WHERE_LABEL[filter.where]}
		</button>
	)

	return (
		<>
			<FilterBar
				facets={facets}
				leading={distanceTrigger}
				extra={whereTrigger}
				activeCount={activeFilterCount(filter)}
				onClearAll={() =>
					setFilter({
						types: [],
						costs: [],
						weathers: [],
						audiences: [],
						accessibilities: [],
						where: 'all',
						maxDistanceMiles: null,
					})
				}
			/>

			<DistanceFilterDialog
				open={distanceOpen}
				value={filter.maxDistanceMiles}
				geo={geo}
				onRetryLocation={onRetryLocation}
				onChange={(v) => setFilter({ ...filter, maxDistanceMiles: v })}
				onClose={() => setDistanceOpen(false)}
			/>
		</>
	)
}
